import { createFileRoute } from "@tanstack/react-router"
import { useState, useMemo } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { SectionHeading, Paragraph, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-fonts-generator")!

export const Route = createFileRoute("/tools/instagram-fonts-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({ title: tool.seo.title, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-fonts-generator" }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-fonts-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-fonts-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-fonts-generator" },
      ]}),
      createHowToSchema({ name: "How to Generate Fancy Fonts for Instagram", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: FontsGeneratorPage,
})

// --- Unicode font conversion helpers ---

function convertChar(char: string, startUpper: number, startLower: number): string {
  const code = char.charCodeAt(0)
  if (code >= 65 && code <= 90) return String.fromCodePoint(startUpper + (code - 65))
  if (code >= 97 && code <= 122) return String.fromCodePoint(startLower + (code - 97))
  return char
}

function convertText(text: string, startUpper: number, startLower: number): string {
  return Array.from(text).map((ch) => convertChar(ch, startUpper, startLower)).join("")
}

// Small caps mapping (lowercase only; uppercase stays normal)
const SMALL_CAPS_MAP: Record<string, string> = {
  a: "\u1D00", b: "\u0299", c: "\u1D04", d: "\u1D05", e: "\u1D07",
  f: "\u0493", g: "\u0262", h: "\u029C", i: "\u026A", j: "\u1D0A",
  k: "\u1D0B", l: "\u029F", m: "\u1D0D", n: "\u0274", o: "\u1D0F",
  p: "\u1D18", q: "\u01EB", r: "\u0280", s: "\u0455", t: "\u1D1B",
  u: "\u1D1C", v: "\u1D20", w: "\u1D21", x: "\u0078", y: "\u028F",
  z: "\u1D22",
}

function toSmallCaps(text: string): string {
  return Array.from(text).map((ch) => SMALL_CAPS_MAP[ch] ?? ch).join("")
}

// Upside-down mapping
const UPSIDE_DOWN_MAP: Record<string, string> = {
  a: "\u0250", b: "q", c: "\u0254", d: "p", e: "\u01DD",
  f: "\u025F", g: "\u0183", h: "\u0265", i: "\u0131", j: "\u027E",
  k: "\u029E", l: "l", m: "\u026F", n: "u", o: "o",
  p: "d", q: "b", r: "\u0279", s: "s", t: "\u0287",
  u: "n", v: "\u028C", w: "\u028D", x: "x", y: "\u028E",
  z: "z",
  A: "\u2200", B: "\u1012", C: "\u0186", D: "\u15E1", E: "\u018E",
  F: "\u2132", G: "\u2141", H: "H", I: "I", J: "\u017F",
  K: "\u22CA", L: "\u2142", M: "W", N: "N", O: "O",
  P: "\u0500", Q: "\u038C", R: "\u1D1A", S: "S", T: "\u22A5",
  U: "\u2229", V: "\u039B", W: "M", X: "X", Y: "\u2144",
  Z: "Z",
  "1": "\u0196", "2": "\u1105", "3": "\u0190", "4": "\u3123",
  "5": "\u03DB", "6": "9", "7": "\u3125", "8": "8", "9": "6", "0": "0",
  ".": "\u02D9", ",": "\u02BB", "?": "\u00BF", "!": "\u00A1",
  "'": ",", '"': "\u201E", "(": ")", ")": "(", "[": "]", "]": "[",
  "{": "}", "}": "{", "<": ">", ">": "<", "&": "\u214B",
  "_": "\u203E",
}

function toUpsideDown(text: string): string {
  return Array.from(text).map((ch) => UPSIDE_DOWN_MAP[ch] ?? ch).reverse().join("")
}

// Strikethrough: add combining long stroke overlay after each visible character
function toStrikethrough(text: string): string {
  return Array.from(text).map((ch) => (ch === " " ? " " : ch + "\u0336")).join("")
}

type FontStyle = {
  name: string
  convert: (text: string) => string
}

const FONT_STYLES: FontStyle[] = [
  { name: "Bold", convert: (t) => convertText(t, 0x1D400, 0x1D41A) },
  { name: "Italic", convert: (t) => convertText(t, 0x1D434, 0x1D44E) },
  { name: "Bold Italic", convert: (t) => convertText(t, 0x1D468, 0x1D482) },
  { name: "Script", convert: (t) => convertText(t, 0x1D49C, 0x1D4B6) },
  { name: "Bold Script", convert: (t) => convertText(t, 0x1D4D0, 0x1D4EA) },
  { name: "Fraktur", convert: (t) => convertText(t, 0x1D504, 0x1D51E) },
  { name: "Double-Struck", convert: (t) => convertText(t, 0x1D538, 0x1D552) },
  { name: "Monospace", convert: (t) => convertText(t, 0x1D670, 0x1D68A) },
  { name: "Circled", convert: (t) => convertText(t, 0x24B6, 0x24D0) },
  { name: "Squared", convert: (t) => convertText(t, 0x1F130, 0x1F130) },
  { name: "Fullwidth", convert: (t) => convertText(t, 0xFF21, 0xFF41) },
  { name: "Small Caps", convert: toSmallCaps },
  { name: "Bubble", convert: (t) => convertText(t, 0x1F150, 0x1F150) },
  { name: "Upside Down", convert: toUpsideDown },
  { name: "Strikethrough", convert: toStrikethrough },
]

const MAX_CHARS = 500

function FontsGeneratorPage() {
  const [text, setText] = useState("")
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const results = useMemo(() => {
    if (!text.trim()) return []
    return FONT_STYLES.map((style) => ({
      name: style.name,
      converted: style.convert(text),
    }))
  }, [text])

  const copyToClipboard = async (value: string, index: number) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = value
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>What Are Unicode Fonts?</SectionHeading>
            <Paragraph className="mb-4">
              When most people hear the word "font," they think of typeface files installed on a computer — things like Arial, Helvetica, or Times New Roman. Unicode fonts are fundamentally different. They are not font files at all. Instead, they are actual characters defined in the Unicode standard, the universal system that assigns a unique number (called a code point) to every letter, symbol, and emoji used in digital communication worldwide. What makes them special is that many of these characters happen to look like stylized versions of the everyday Latin alphabet you already know.
            </Paragraph>
            <Paragraph className="mb-4">
              Because Unicode "fonts" are real characters rather than visual formatting applied on top of text, they travel with the text itself. Copy them into an Instagram bio, a Tweet, a YouTube comment, a WhatsApp message, or an email subject line and they will appear styled exactly as intended. There is no CSS, no rich-text editor, and no special rendering engine required. Every modern device, operating system, and browser knows how to display Unicode characters, which means your followers see the styled text whether they are on an iPhone, an Android phone, a Windows laptop, or a Mac.
            </Paragraph>
            <Paragraph className="mb-4">
              This is precisely why Unicode fonts are the only reliable way to use custom-looking text on Instagram. The platform strips all HTML and rich-text formatting from user input. Bold and italic buttons do not exist in the Instagram caption editor. Yet when you paste a string of Unicode characters that visually resemble bold or script letters, Instagram accepts them without question because, from its perspective, you are simply typing ordinary text. The result is eye-catching typography in a space where everyone else is limited to the same default font.
            </Paragraph>
            <Paragraph>
              Unicode fonts work everywhere plain text is accepted: bios, captions, comments, direct messages, story text overlays, Reels descriptions, and even your display name. They are the simplest, most portable way to add personality and visual hierarchy to your Instagram presence without relying on image-based workarounds or third-party apps that require special permissions.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>How Instagram Fancy Fonts Work</SectionHeading>
            <Paragraph className="mb-4">
              Every character you can type or display on a screen has a numeric identity called a code point. The standard Latin letter "A" lives at code point U+0041. But Unicode contains far more than one version of the letter A. The <strong className="text-neutral-800 dark:text-neutral-200">Mathematical Alphanumeric Symbols</strong> block (U+1D400 through U+1D7FF) defines complete sets of bold, italic, bold-italic, script, bold-script, fraktur, double-struck, sans-serif, sans-serif bold, sans-serif italic, sans-serif bold-italic, and monospace letters. Each set maps A through Z and a through z to a unique range of code points. For example, the mathematical bold capital A sits at U+1D400, while the mathematical script capital A sits at U+1D49C.
            </Paragraph>
            <Paragraph className="mb-4">
              When this tool converts your text, it takes each letter and replaces it with the corresponding character from the chosen Unicode range. The letter "H" typed in the Script style becomes the code point for mathematical script capital H. Instagram, your browser, and your phone all recognize this code point and render the appropriate glyph. No font file is downloaded, no style sheet is applied — the character itself carries its visual appearance.
            </Paragraph>
            <Paragraph>
              Additional styles like Circled, Squared, and Fullwidth letters come from other Unicode blocks originally designed for specialized typographic or regional use. Circled letters (U+24B6 onward) were created for list numbering in East Asian documents. Fullwidth letters (U+FF21 onward) match the width of CJK characters for alignment in mixed-script text. Repurposing these characters as decorative fonts for social media is a creative side effect of Unicode's enormous character inventory, and it works flawlessly because every platform is required to support the standard.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Best Uses for Fancy Fonts on Instagram</SectionHeading>
            <FeatureGrid items={[
              { icon: "✨", title: "Bio Headlines", description: "Your Instagram bio is the first thing visitors read when they land on your profile. Using a bold or script Unicode font for your name, title, or tagline creates instant visual separation from the rest of the bio text. A styled headline draws the eye and communicates professionalism or creativity before a single word of your actual content is read." },
              { icon: "💬", title: "Caption Emphasis", description: "Long captions benefit from strategic use of styled text. Bolding a key phrase or switching one sentence to italic script helps break up a wall of text and directs readers to the most important takeaway. Think of it like using a highlighter — a single accent draws attention without overwhelming the reader." },
              { icon: "📱", title: "Story Text Overlays", description: "While Instagram Stories offer a handful of built-in fonts, the selection is limited and identical for every user. Pasting Unicode-styled text into the story text box gives you access to styles Instagram does not provide natively, helping your stories stand out in a crowded feed of taps." },
              { icon: "✍️", title: "Comment Signatures", description: "Creators who engage actively in comment sections can use a subtle styled signature — like their name in small caps or a short tagline in script — to make their replies instantly recognizable among hundreds of comments on a viral post." },
              { icon: "🎨", title: "Brand Differentiation", description: "Consistent use of a particular Unicode style across your profile, captions, and comments builds a recognizable visual identity. Followers begin to associate the font style with your brand, creating a subtle but effective layer of branding that costs nothing and requires no design tools." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Font Style Guide</SectionHeading>
            <FeatureGrid items={[
              { title: "Bold — Authority and Clarity", description: "Bold Unicode text commands attention instantly. Use it for headlines in your bio, the opening hook of a caption, or any phrase you want to anchor the reader's eye to. It conveys confidence and works especially well for business accounts, coaches, and educators who want to project authority. Avoid bolding entire paragraphs — the impact is strongest when used selectively." },
              { title: "Script — Elegance and Personality", description: "Script (cursive) fonts add a handwritten, elegant quality to your text. They are ideal for lifestyle brands, wedding planners, beauty influencers, and anyone whose aesthetic leans toward sophistication. Use script for your name in your bio, a quote in a caption, or a call-to-action that you want to feel personal and inviting rather than corporate." },
              { title: "Monospace — Tech and Minimalism", description: "Monospace letters have equal character widths, giving them a typewriter or terminal feel. This style resonates with tech creators, developers, productivity accounts, and minimalist brands. It signals precision and intentionality. Try it for code snippets in captions, step-by-step lists, or any content where a clean, structured look supports the message." },
              { title: "Circled — Playfulness and Lists", description: "Circled letters wrap each character in a ring, producing a bubbly, badge-like appearance. They are perfect for numbered lists, playful headers, or any content aimed at a younger or more casual audience. Food bloggers, travel creators, and meme pages often use circled text to inject personality and make section dividers feel more fun." },
              { title: "Fraktur — Edgy and Artistic Vibes", description: "Fraktur (blackletter) text has a gothic, old-world feel that immediately sets a dramatic tone. It works well for music artists, tattoo studios, streetwear brands, and anyone cultivating an edgy or alternative aesthetic. Use it sparingly for display text — a single word or short phrase in Fraktur makes a strong visual statement, while full sentences become difficult to read." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Best Practices and Accessibility</SectionHeading>
            <Paragraph className="mb-4">
              Unicode fonts are a powerful styling tool, but they come with important trade-offs that every creator should understand before using them extensively.
            </Paragraph>
            <TipList tips={[
              { title: "Don't overuse styled text.", description: "A full paragraph in script or fraktur is significantly harder to read than regular text. Use fancy fonts as accents — a headline, a single emphasized word, or a short tagline. The surrounding regular text provides contrast that makes the styled portion pop." },
              { title: "Screen readers struggle with Unicode fonts.", description: "Assistive technologies like VoiceOver and TalkBack may read mathematical bold \"A\" as \"mathematical bold capital A\" instead of just \"A,\" making your content confusing or unintelligible for visually impaired users. Always pair styled text with enough regular text that the core message remains accessible." },
              { title: "Mix styled and regular text intentionally.", description: "The most effective approach is to use a Unicode font for a short header or key phrase, then write the body of your caption in standard characters. This creates visual hierarchy without sacrificing readability or accessibility." },
              { title: "Test on multiple devices before posting.", description: "While Unicode is a universal standard, the actual glyph rendering depends on the font files installed on each device. Some older Android phones may show empty boxes or question marks for less common characters. Always preview your text on at least one iOS and one Android device before publishing." },
              { title: "Never use Unicode fonts in hashtags or searchable text.", description: "Instagram's search engine and hashtag system only recognize standard ASCII characters. A hashtag written in bold Unicode will not be clickable, will not appear in search results, and will not count toward any hashtag page. Keep all hashtags, @mentions, and URLs in plain text." },
              { title: "Consider your audience.", description: "Fancy fonts are a style choice that should match your brand voice. A financial advisor's audience expects clarity and professionalism — excessive decorative text may undermine trust. A music artist's audience, on the other hand, may welcome creative typography as part of the artistic expression. Let your content goals guide your font choices." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* Input Card */}
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
            Enter your text
          </label>
          <textarea
            value={text}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setText(e.target.value)
            }}
            placeholder="Type something like: Hello Instagram"
            rows={3}
            className="w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-accent/40 focus:border-brand-accent transition-all resize-none"
          />
          <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500 text-right">
            {text.length}/{MAX_CHARS}
          </p>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-6 space-y-3">
            {results.map((result, index) => (
              <div
                key={result.name}
                className="flex items-center gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 px-4 py-3 shadow-sm"
              >
                <span className="shrink-0 w-28 sm:w-32 text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400">
                  {result.name}
                </span>
                <p className="flex-1 min-w-0 text-sm sm:text-base text-neutral-900 dark:text-white break-all leading-relaxed">
                  {result.converted}
                </p>
                <button
                  onClick={() => copyToClipboard(result.converted, index)}
                  className={`shrink-0 h-8 px-3 rounded-lg text-xs font-semibold transition-all duration-300 ${
                    copiedIndex === index
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-[0_2px_8px_rgba(244,63,94,0.15)] hover:shadow-[0_4px_14px_rgba(244,63,94,0.15)] hover:-translate-y-0.5"
                  }`}
                >
                  {copiedIndex === index ? "Copied!" : "Copy"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {text.trim() === "" && (
          <div className="mt-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">
              Start typing above to see your text in 15 different font styles
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  )
}
